import api from './api';

export const documentsService = {
  /**
   * Sube un documento genérico asociado a un agente.
   * @param {File} file - Archivo a subir
   * @param {Object} options - { agentId, description, tags }
   * @param {Function} onProgress - Callback de progreso (0-100)
   */
  upload: async (file, options = {}, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.agentId) formData.append('agent_id', options.agentId);
    if (options.tenantId) formData.append('tenant_id', options.tenantId);
    if (options.subTenantId) formData.append('sub_tenant_id', options.subTenantId);
    if (options.description) formData.append('description', options.description);
    if (options.tags?.length) formData.append('tags', options.tags.join(','));
    if (options.periodStart) formData.append('period_start', options.periodStart);
    if (options.periodEnd) formData.append('period_end', options.periodEnd);
    if (options.periodLabel) formData.append('period_label', options.periodLabel);

    const response = await api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 min para archivos grandes
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    });
    return response.data;
  },

  /** Lista documentos del tenant, opcionalmente filtrados por agente */
  list: async (agentId = null, fileType = null) => {
    const params = {};
    if (agentId) params.agent_id = agentId;
    if (fileType) params.file_type = fileType;
    const response = await api.get('/documents/', { params });
    return response.data;
  },

  /** Verifica la integridad SHA-256 de un documento */
  verify: async (documentId) => {
    const response = await api.get(`/documents/${documentId}/verify`);
    return response.data;
  },

  /** Elimina un documento */
  delete: async (documentId) => {
    await api.delete(`/documents/${documentId}`);
  },

  /** Descarga un documento (devuelve Blob) */
  download: async (documentId, filename) => {
    const response = await api.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export const financialService = {
  /** Sube y analiza un documento financiero en un solo paso */
  uploadAndAnalyze: async (file, options = {}, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.companyId) formData.append('company_id', options.companyId);
    if (options.agentId) formData.append('agent_id', options.agentId);
    if (options.tenantId) formData.append('tenant_id', options.tenantId);
    if (options.subTenantId) formData.append('sub_tenant_id', options.subTenantId);
    formData.append('framework', options.framework || 'niif_pymes');
    const companyTypeCode = options.companyTypeCode || options.entityType;
    if (companyTypeCode) formData.append('company_type_code', companyTypeCode);
    // Período obligatorio para el agente financiero
    if (options.periodStart) formData.append('period_start', options.periodStart);
    if (options.periodEnd) formData.append('period_end', options.periodEnd);
    if (options.periodLabel) formData.append('period_label', options.periodLabel);

    const response = await api.post('/financial/upload-and-analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: options.timeoutMs ?? 900000, // 15 min para análisis pesado
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    });
    return response.data;
  },

  /** Sube documento financiero y encola análisis en background */
  uploadAndAnalyzeAsync: async (file, options = {}, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.companyId) formData.append('company_id', options.companyId);
    if (options.agentId) formData.append('agent_id', options.agentId);
    if (options.tenantId) formData.append('tenant_id', options.tenantId);
    if (options.subTenantId) formData.append('sub_tenant_id', options.subTenantId);
    formData.append('framework', options.framework || 'niif_pymes');
    const companyTypeCode = options.companyTypeCode || options.entityType;
    if (companyTypeCode) formData.append('company_type_code', companyTypeCode);
    if (options.periodStart) formData.append('period_start', options.periodStart);
    if (options.periodEnd) formData.append('period_end', options.periodEnd);
    if (options.periodLabel) formData.append('period_label', options.periodLabel);

    const response = await api.post('/financial/upload-and-analyze-async', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: options.timeoutMs ?? 120000,
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    });
    return response.data;
  },

  /** Consulta estado de tarea asíncrona de análisis financiero */
  getAnalysisTask: async (taskId, options = {}) => {
    const params = {};
    if (options.tenantId) params.tenant_id = options.tenantId;
    const response = await api.get(`/financial/analysis-tasks/${taskId}`, { params });
    return response.data;
  },

  /** Analiza un documento ya subido */
  analyze: async (documentId, framework = 'niif_pymes') => {
    const response = await api.post(`/financial/analyze/${documentId}`, null, {
      params: { framework },
    });
    return response.data;
  },

  /** Crea una empresa para análisis */
  createCompany: async (data) => {
    const response = await api.post('/financial/companies', data);
    return response.data;
  },

  /** Lista empresas del tenant */
  listCompanies: async () => {
    const response = await api.get('/financial/companies');
    return response.data;
  },

  /** Inicia una nueva sesión de chat financiero */
  newSession: async () => {
    const response = await api.post('/financial/chat/new-session');
    return response.data.session_id;
  },

  /** Envía un mensaje al agente financiero */
  chat: async (message, sessionId, companyId = null, documentId = null) => {
    const response = await api.post('/financial/chat', {
      message,
      session_id: sessionId,
      company_id: companyId,
      document_id: documentId,
    });
    return response.data;
  },

  /** Análisis vertical de un documento */
  verticalAnalysis: async (documentId) => {
    const response = await api.get(`/financial/analysis/vertical/${documentId}`);
    return response.data;
  },

  /**
   * Paso 1 del flujo de dos etapas:
   * Sube el documento, lo parsea e identifica automáticamente el tipo de estado.
   * @param {File} file - Archivo a subir
   * @param {{ periodStart, periodEnd, periodLabel, companyId }} options
   * @param {(pct: number) => void} [onProgress] - Callback de progreso 0-100
   * @returns {Promise<{ document_id, filename, sha256, period, file_type, sheets_found, identification, next_step }>}
   */
  uploadPreliminary: async (file, options = {}, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.companyId) formData.append('company_id', String(options.companyId));
    if (options.tenantId) formData.append('tenant_id', options.tenantId);
    if (options.subTenantId) formData.append('sub_tenant_id', options.subTenantId);
    formData.append('period_start', options.periodStart);
    formData.append('period_end', options.periodEnd);
    if (options.periodLabel) formData.append('period_label', options.periodLabel);

    const response = await api.post('/financial/upload-preliminary', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 min
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total ?? e.loaded)))
        : undefined,
    });
    return response.data;
  },

  /**
   * Paso 2 del flujo de dos etapas:
   * Confirma el tipo de estado y ejecuta el análisis completo.
   * @param {number} documentId - ID del documento subido en uploadPreliminary
   * @param {string} statementType - Tipo confirmado
   * @param {string} [framework] - Marco normativo, por defecto 'niif_pymes'
   * @returns {Promise<object>} Resultado completo del análisis
   */
  confirmAndAnalyze: async (
    documentId,
    statementType,
    framework = 'niif_pymes',
    companyTypeCode = null,
    options = {},
  ) => {
    const formData = new FormData();
    formData.append('statement_type', statementType);
    formData.append('framework', framework);
    if (companyTypeCode) formData.append('company_type_code', companyTypeCode);
    if (options.tenantId) formData.append('tenant_id', options.tenantId);
    if (options.subTenantId) formData.append('sub_tenant_id', options.subTenantId);

    const response = await api.post(
      `/financial/confirm-and-analyze/${documentId}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // 3 min para análisis completo
      },
    );
    return response.data;
  },

  /** Lista tipos de empresa disponibles en catálogo */
  listCompanyTypes: async () => {
    const response = await api.get('/catalogs/company-types');
    return response.data;
  },

  /** Lista normas contables permitidas para un tipo de empresa */
  listAccountingFrameworksByCompanyType: async (companyTypeCode) => {
    const response = await api.get(`/catalogs/company-types/${companyTypeCode}/accounting-frameworks`);
    return response.data;
  },

  /** Lista marcos contables disponibles */
  listAccountingFrameworks: async () => {
    const response = await api.get('/catalogs/accounting-frameworks');
    return response.data;
  },

  /** Lista tipos de empresa permitidos para un marco contable */
  listCompanyTypesByAccountingFramework: async (accountingFrameworkCode) => {
    const response = await api.get(`/catalogs/accounting-frameworks/${accountingFrameworkCode}/company-types`);
    return response.data;
  },

  /** Valida combinación tipo de empresa + norma contable */
  validateAccountingFramework: async (companyTypeCode, accountingFrameworkCode) => {
    const response = await api.post('/catalogs/validate-accounting-framework', {
      company_type_code: companyTypeCode,
      accounting_framework_code: accountingFrameworkCode,
    });
    return response.data;
  },
};
